import React from 'react';
import Cell from './Cell';
import GhostCell from './GhostCell';
import { Stage as StageType, Position } from '../types';

interface StageProps {
  stage: StageType;
  // Optional ghost preview positions
  ghostCells?: Position[];
  // Callback for double-tap (or double-click) to trigger hard drop
  onDoubleTap?: () => void;
  // Layout adjustment for mobile
  isMobile?: boolean;
  // Whether a piece was just locked
  pieceLocked?: boolean;
  // Whether to show new piece entry animation
  newPieceEntry?: boolean;
}

// Game area grid
const Stage: React.FC<StageProps> = ({ 
  stage, 
  ghostCells = [], 
  onDoubleTap, 
  isMobile = false,
  pieceLocked = false,
  newPieceEntry = false,
}) => {
  // Track taps to detect double-tap for hard drop
  const lastTap = React.useRef<number | null>(null);
  const handleTouchEnd = () => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300; // ms threshold
    if (lastTap.current && now - lastTap.current < DOUBLE_TAP_DELAY) {
      onDoubleTap && onDoubleTap();
    }
    lastTap.current = now;
  };
  
  // Build a map of ghost cell keys for fast lookup, with tetromino type
  const ghostMap = new Map();
  ghostCells.forEach((pos, index) => {
    // Use first non-empty cell in stage to determine the tetromino type
    const cell = stage[Math.max(0, Math.min(pos.y, stage.length - 1))][Math.max(0, Math.min(pos.x, stage[0].length - 1))];
    ghostMap.set(`${pos.y}-${pos.x}`, cell[0] || '');
  });
  
  // Responsive container styling
  const aspect = `${stage[0].length}/${stage.length}`;
  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateRows: `repeat(${stage.length}, 1fr)`,
    gridTemplateColumns: `repeat(${stage[0].length}, 1fr)`,
    aspectRatio: aspect,
    background: 'linear-gradient(180deg, #222 0%, #111 100%)',
    border: '4px solid #333',
    borderRadius: '4px',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: pieceLocked ? '0 0 15px rgba(255, 255, 255, 0.5)' : 'none',
    transition: 'box-shadow 0.2s ease-out'
  };
  
  if (isMobile) {
    gridStyle.width = '90vw';
  } else {
    gridStyle.height = '80vh';
  }
  
  return (
    <div
      onDoubleClick={onDoubleTap}
      onTouchEnd={handleTouchEnd}
      style={gridStyle}
      className={newPieceEntry ? 'new-piece-entry' : ''}
    >
      {stage.map((row, y) =>
        row.map((cell, x) => {
          const key = `${y}-${x}`;
          // Render ghost preview if this position is in ghostCells and empty
          if (ghostMap.has(key) && cell[0] === 0) {
            return <GhostCell key={`g-${key}`} type={ghostMap.get(key)} />;
          }
          // Check if this is part of a new piece for entry animation
          const isNewPiece = newPieceEntry && cell[0] !== 0 && cell[1] !== 'merged';
          return <Cell key={key} cell={cell} isNew={isNewPiece} />;
        })
      )}
    </div>
  );
};

export default Stage;